/**
 * 管理员后台 - 路由配置
 * 独立的管理后台路由，使用 /csdadmin 前缀
 */

import type { RouteRecordRaw } from 'vue-router';

// 管理后台路由配置
export const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/csdadmin',
    redirect: '/csdadmin/dashboard'
  },
  {
    path: '/csdadmin/login',
    name: 'AdminLogin',
    component: () => import('@/views/admin/Login.vue'),
    meta: {
      title: '管理员登录',
      requiresAdminAuth: false,
      hideAdminLayout: true,
      hideLayout: true
    }
  },
  {
    path: '/csdadmin',
    component: () => import('@/views/admin/Layout.vue'),
    meta: {
      requiresAdminAuth: true,
      hideLayout: true
    },
    children: [
      // 数据概览
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/Dashboard.vue'),
        meta: {
          title: '数据概览',
          icon: 'dashboard'
        }
      },
      // 作品审核
      {
        path: 'works',
        name: 'AdminWorks',
        component: () => import('@/views/admin/works/Index.vue'),
        meta: {
          title: '作品审核',
          icon: 'video-camera',
          permission: 'work_review'
        }
      },
      // 用户管理
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/views/admin/users/Index.vue'),
        meta: {
          title: '用户管理',
          icon: 'user',
          permission: 'work_review'
        }
      },
      {
        path: 'users/:userId/projects',
        name: 'AdminUserProjects',
        component: () => import('@/views/admin/users/Projects.vue'),
        meta: {
          title: '用户项目',
          hidden: true,
          permission: 'work_review'
        }
      },
      {
        path: 'users/:userId/projects/:storyId',
        name: 'AdminUserProjectDetail',
        component: () => import('@/views/admin/users/ProjectDetail.vue'),
        meta: {
          title: '项目详情',
          hidden: true,
          permission: 'work_review'
        }
      },
      // 评论管理
      {
        path: 'comments',
        name: 'AdminComments',
        component: () => import('@/views/admin/comments/Index.vue'),
        meta: {
          title: '评论管理',
          icon: 'comment',
          permission: 'comment_manage'
        }
      },
      // 素材管理
      {
        path: 'assets',
        name: 'AdminAssets',
        redirect: '/csdadmin/assets/avatars',
        meta: {
          title: '素材管理',
          icon: 'picture',
          permission: 'asset_manage'
        },
        children: [
          {
            path: 'avatars',
            name: 'AdminAvatars',
            component: () => import('@/views/admin/assets/Avatars.vue'),
            meta: { title: '形象库' }
          },
          {
            path: 'scenes',
            name: 'AdminScenes',
            component: () => import('@/views/admin/assets/Scenes.vue'),
            meta: { title: '场景库' }
          },
          // 道具库暂时隐藏
          // {
          //   path: 'props',
          //   name: 'AdminProps',
          //   component: () => import('@/views/admin/assets/Props.vue'),
          //   meta: { title: '道具库' }
          // }
        ]
      },
      // 模板管理
      {
        path: 'templates',
        name: 'AdminTemplates',
        redirect: '/csdadmin/templates/voices',
        meta: {
          title: '模板管理',
          icon: 'appstore',
          permission: 'template_manage'
        },
        children: [
          {
            path: 'voices',
            name: 'AdminVoices',
            component: () => import('@/views/admin/templates/Voices.vue'),
            meta: { title: '音色模板' }
          },
          {
            path: 'styles',
            name: 'AdminStyles',
            component: () => import('@/views/admin/templates/Styles.vue'),
            meta: { title: '视觉风格' }
          },
          {
            path: 'music',
            name: 'AdminMusic',
            component: () => import('@/views/admin/templates/Music.vue'),
            meta: { title: '背景音乐' }
          },
          {
            path: 'stories',
            name: 'AdminStoryTemplates',
            component: () => import('@/views/admin/templates/Stories.vue'),
            meta: { title: '故事模板' }
          }
        ]
      },
      // 标签管理
      {
        path: 'tags',
        name: 'AdminTags',
        component: () => import('@/views/admin/tags/Index.vue'),
        meta: {
          title: '标签管理',
          icon: 'tags',
          permission: 'tag_manage'
        }
      },
      // 任务监控
      {
        path: 'tasks',
        name: 'AdminTasks',
        component: () => import('@/views/admin/tasks/Index.vue'),
        meta: {
          title: '任务监控',
          icon: 'thunderbolt',
          permission: 'task_monitor'
        }
      },
      {
        path: 'video-exports',
        name: 'AdminVideoExports',
        redirect: '/csdadmin/tasks?tab=video-export',
        meta: {
          title: '导出管理',
          icon: 'video-camera',
          permission: 'task_monitor',
          hidden: true
        }
      },
      // 链路监控
      {
        path: 'monitor',
        name: 'AdminMonitor',
        component: () => import('@/views/admin/monitor/Index.vue'),
        meta: {
          title: '链路监控',
          icon: 'monitor',
          permission: 'task_monitor'
        }
      },
      // API 用量监控
      {
        path: 'api-usage',
        name: 'AdminApiUsage',
        component: () => import('@/views/admin/monitor/ApiUsage.vue'),
        meta: {
          title: 'API用量',
          icon: 'bar-chart',
          permission: 'task_monitor'
        }
      },
      // 系统配置
      {
        path: 'config',
        name: 'AdminConfig',
        redirect: '/csdadmin/config/ai',
        meta: {
          title: '系统配置',
          icon: 'setting',
          permission: 'config_manage'
        },
        children: [
          {
            path: 'ai',
            name: 'AdminConfigAI',
            component: () => import('@/views/admin/config/AI.vue'),
            meta: { title: 'AI配置' }
          },
          {
            path: 'sensitive-words',
            name: 'AdminSensitiveWords',
            component: () => import('@/views/admin/config/SensitiveWords.vue'),
            meta: { title: '敏感词管理', permission: 'sensitive_word' }
          },
          {
            path: 'announcements',
            name: 'AdminAnnouncements',
            component: () => import('@/views/admin/config/Announcements.vue'),
            meta: { title: '公告管理', permission: 'announcement' }
          }
        ]
      },
      // 操作日志
      {
        path: 'logs',
        name: 'AdminLogs',
        component: () => import('@/views/admin/logs/Index.vue'),
        meta: {
          title: '操作日志',
          icon: 'file-text',
          permission: 'operation_log'
        }
      },
      // 意见反馈
      {
        path: 'feedbacks',
        name: 'AdminFeedbacks',
        component: () => import('@/views/admin/Feedbacks.vue'),
        meta: {
          title: '意见反馈',
          icon: 'message'
        }
      },
      // 通知管理
      {
        path: 'notifications',
        name: 'AdminNotifications',
        component: () => import('@/views/admin/Notifications.vue'),
        meta: {
          title: '通知管理',
          icon: 'notification'
        }
      },
      // 管理员管理
      {
        path: 'admins',
        name: 'AdminManage',
        component: () => import('@/views/admin/admins/Index.vue'),
        meta: {
          title: '管理员管理',
          icon: 'team',
          permission: 'admin_manage'
        }
      }
    ]
  }
];

export default adminRoutes;
